import { HttpClient } from '@angular/common/http';
import { AidLoaderStyle, AidStyleKey } from '../pages/chat-styling/aid-style';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { mergeById } from '../helpers/merge';
import { StateCleanupService } from './state-cleanup.service';

export type ChatBubbleStyle = 'dot' | 'blob' | 'glass';

/** Pop-up-Nachricht über der geschlossenen Chat-Blase — die proaktive Frage
 *  mit Antwort-Buttons (chat-widget `app-interactive-question` +
 *  `app-message-replies`). Transparenz 0–100 % gilt nur für den Hintergrund
 *  (0 = deckend), die Schrift bleibt immer voll deckend. Fehlt ein Wert,
 *  folgt das Widget dem Theme: Hintergrund `secondaryColor`, Schrift
 *  `botTextColor`, Antwort-Buttons #ffffff / 25 % / #000000. */
export interface ChatPopupStyles {
  popupBackgroundColor: string;
  popupTransparency: number;
  popupTextColor: string;
  popupReplyBackgroundColor: string;
  popupReplyTransparency: number;
  popupReplyTextColor: string;
}

/** Interaktive Lernhilfen (Reiter „Lernhilfen“): `null`/fehlend = „Automatisch“
 *  (Standard bzw. Primärfarbe, `resolveAidStyle` in
 *  pages/chat-styling/aid-style.ts), sonst der gesetzte Wert. */
export type ChatAidStyles = Partial<Record<AidStyleKey, string | number | null>> & {
  aidLoader?: AidLoaderStyle;
};

/** Aufbau des Chat-Widgets (Feld „Widget-Design“): 'window' = bisheriges
 *  Fenster mit Reitern, 'classic' / 'liquid' = neues Design „Orbi Command“
 *  (Orbi + Eingabeleiste, Apps seitlich) flach bzw. mit Glanz. '' / fehlend =
 *  automatisch: Agents von vor dem neuen Design behalten das Fenster, neue
 *  starten mit 'liquid' (al-tutor `resolve_widget_design`, Migration 236). */
export type WidgetDesign = '' | 'window' | 'classic' | 'liquid';

export interface ChatSettingStyles extends Partial<ChatPopupStyles>, ChatAidStyles {
  alphabeesBranding: boolean;
  widgetDesign?: WidgetDesign;
  backgroundColor: string;
  botTextColor: string;
  userTextColor: string;
  /** Hintergrund der Nutzer-Nachricht (Vollton oder Verlauf, seit
   *  09.10.2026). Fehlt er, ist die Blase im Widget weiß. */
  userBubbleColor?: string;
  chatBubbleIcon: string;
  chatBubbleIconSize: number;
  /** Liquid: Farbe der KI-Sterne bzw. eines SVG-Icons im Dot/Blob. */
  chatBubbleIconColor?: string;
  /** Liquid: Farbe der Symbole und Schrift auf der Primärfarbe (Apps,
   *  Vorschläge, Senden); ungesetzt = weiß. */
  appIconColor?: string;
  chatPostion: string;
  /** @deprecated retained for backward compat — derive from chatBubbleStyle. */
  enableChatBubbleAnimation?: boolean;
  chatBubbleStyle: ChatBubbleStyle;
  fontFamily: string;
  headerName: string;
  inputPlaceholder: string;
  logo: string;
  logoSize: number;
  primaryColor: string;
  secondaryColor: string;
}

export interface ChatSettings {
  id: string;
  widget_instance?: string;
  is_default: boolean;
  styles: ChatSettingStyles;
}

/** Standard-Logo (Orbi). Die Datei liefert das chat-widget mit
 *  (`services/chat-widget/src/assets/logos/`), je Umgebung neben seinem
 *  Loader — daher relativ zu `chatWidgetUrl` statt einer Portal-URL. */
export const DEFAULT_CHAT_LOGO_URL = new URL(
  'assets/logos/orbi-logo-v1.png',
  environment.chatWidgetUrl
).href;

/** The default chat styling — what an agent shows when no chat style is
 *  assigned to it (al-tutor sends `styles: {}` / `styles_name: "default"`
 *  and the widget falls back to its built-in values). Single source for the
 *  Chat-Styling page: the example widget while the client has no styles yet,
 *  and the starting values of a newly created style. Keep in sync with the
 *  widget's built-in defaults in
 *  `services/chat-widget/src/app/services/settings.service.ts`.
 *  `inputPlaceholder` stays empty here — it is language-dependent and is
 *  resolved via i18n (`pages.chatStyling.form.inputPlaceholder.default` /
 *  `pages.chatStyling.preview.defaultPlaceholder`). Standard seit
 *  01.10.2026: Orbi als Chat-Blase (`glass`) und als Logo, Arial.
 *  Die `popup*`-Werte gibt es nur hier: das Widget hat für sie bewusst
 *  keinen Standard (fehlt der Schlüssel, folgt es dem Theme) — sie bilden
 *  genau das nach, was es beim Standard-Design zeigt. */
/** Theme-Farben, denen die Pop-up-Nachricht im Widget ohne eigene Werte
 *  folgt — als Konstanten, damit Standard und Pop-up-Standard nicht
 *  auseinanderlaufen können. */
const DEFAULT_SECONDARY_COLOR = '#3B82F6';
const DEFAULT_BOT_TEXT_COLOR = '#ffffff';

export const DEFAULT_CHAT_STYLES: Omit<
  ChatSettingStyles,
  'chatPostion' | 'headerName' | 'enableChatBubbleAnimation'
> &
  ChatPopupStyles = {
  alphabeesBranding: true,
  backgroundColor: '#f8f8f8',
  botTextColor: DEFAULT_BOT_TEXT_COLOR,
  userTextColor: '#003598',
  userBubbleColor: '#ffffff',
  chatBubbleIcon: '',
  chatBubbleIconSize: 36,
  chatBubbleStyle: 'glass',
  fontFamily: 'Arial',
  inputPlaceholder: '',
  logo: DEFAULT_CHAT_LOGO_URL,
  logoSize: 42,
  primaryColor: '#003598',
  secondaryColor: DEFAULT_SECONDARY_COLOR,
  popupBackgroundColor: DEFAULT_SECONDARY_COLOR,
  popupTransparency: 0,
  popupTextColor: DEFAULT_BOT_TEXT_COLOR,
  popupReplyBackgroundColor: '#ffffff',
  popupReplyTransparency: 25,
  popupReplyTextColor: '#000000',
};

@Injectable({
  providedIn: 'root',
})
export class ChatService {
  chatSettings$ = new BehaviorSubject<ChatSettings[]>([]);

  loading$ = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private stateCleanupService: StateCleanupService
  ) {
    this.listChatSettings().subscribe();
    this.stateCleanupService.register(() => this.resetState());
  }

  listChatSettings() {
    this.loading$.next(true);
    return this.http
      .get(`${environment.tutorsBackend}/tutor/settings-chat/list`)
      .pipe(
        map((data: any) => data.data),
        tap((data: any) => {
          this.chatSettings$.next(data);
          this.loading$.next(false);
        })
      );
  }

  getChatSettings(id: string): Observable<ChatSettings> {
    const currentSettings = this.chatSettings$.value.find(
      (settings) => settings.id === id
    );

    console.log('currentSettings', currentSettings);
    if (currentSettings) {
      return new BehaviorSubject(currentSettings);
    }

    this.loading$.next(true);
    return this.http
      .get(`${environment.tutorsBackend}/tutor/settings-chat/${id}`)
      .pipe(
        tap((data: any) => {
          this.chatSettings$.next(
            mergeById(this.chatSettings$.value, data.data)
          );
          this.loading$.next(false);
        })
      );
  }

  createChatSettings(chatSettings: Partial<ChatSettings>) {
    this.loading$.next(true);
    return this.http
      .post(`${environment.tutorsBackend}/tutor/settings-chat`, {
        ...chatSettings,
      })
      .pipe(
        tap((data: any) => {
          this.chatSettings$.next(
            mergeById(this.chatSettings$.value, data.data)
          );
          this.loading$.next(false);
        })
      );
  }

  updateChatSettings(chatSettings: ChatSettings) {
    this.loading$.next(true);
    return this.http
      .patch(
        `${environment.tutorsBackend}/tutor/settings-chat/${chatSettings.id}`,
        { ...chatSettings }
      )
      .pipe(
        tap((data: any) => {
          this.chatSettings$.next(
            mergeById(this.chatSettings$.value, data.data)
          );
          this.loading$.next(false);
        }),
        catchError((err) => {
          this.loading$.next(false);
          return throwError(() => err);
        })
      );
  }

  deleteChatSettings(id: string) {
    this.loading$.next(true);
    return this.http
      .delete(`${environment.tutorsBackend}/tutor/settings-chat/${id}`)
      .pipe(
        tap(() => {
          this.chatSettings$.next(
            this.chatSettings$.value.filter(
              (chatSettings) => chatSettings.id !== id
            )
          );
          this.loading$.next(false);
        })
      );
  }

  private resetState() {
    this.chatSettings$.next([]);
    this.loading$.next(false);
  }
}
